import React, { useRef, useState, useCallback } from 'react';
import { IContainerHistoryPoint } from '../interfaces/IContainerHistoryPoint';
import { IContainerScanEvent } from '../interfaces/IContainerScanEvent';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
    AXIS_TOP, MARKER_TOP, timelineStyles,
    tickStyle, tickLabelStyle, historyPointStyle,
    scanLineStyle, scanMarkerStyle, infoLineStyle,
} from './containerTimeline.styles';


interface Props {
    historyPoints: IContainerHistoryPoint[];
    scanEvents: IContainerScanEvent[];
    selectedPoint: IContainerHistoryPoint | undefined;
    onSelectPoint: (point: IContainerHistoryPoint) => void;
}

export const ContainerTimeline: React.FC<Props> = ({ historyPoints, scanEvents, selectedPoint, onSelectPoint }) => {
    const { t } = useTranslation("admin");
    const [hoverText, setHoverText] = useState<string>('');
    const [copied, setCopied] = useState(false);

    const handleMouseLeave = useCallback(() => setHoverText(''), []);

    const handleCopy = () => {
        if (!hoverText) return;
        navigator.clipboard.writeText(hoverText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    if (historyPoints.length === 0) return null;

    const sorted = [...historyPoints].sort((a, b) => a.lastActiveDate.getTime() - b.lastActiveDate.getTime());
    const allTimes = [
        ...sorted.map(p => p.lastActiveDate.getTime()),
        ...scanEvents.map(e => e.date.getTime()),
    ];
    const minDate = Math.min(...allTimes);
    const maxDate = Math.max(...allTimes);
    const span = maxDate - minDate || 1;

    const pct = (date: Date) => ((date.getTime() - minDate) / span * 100);

    const dateLabels = buildDateLabels(minDate, maxDate);

    const rpCount = historyPoints.filter(p => p.receptionPlaceName && !p.internalStoreName).length;
    const magCount = historyPoints.filter(p => p.internalStoreName).length;
    const roadCount = historyPoints.filter(p => !p.receptionPlaceName && !p.internalStoreName).length;

    return (
        <div style={timelineStyles.wrapper}>
            <div style={timelineStyles.legend}>
                <LegendItem color="#378ADD" shape="circle" label={`${t("containers.timeline.receptionPlace")} (${rpCount})`} />
                <LegendItem color="#7F77DD" shape="square" label={`${t("containers.timeline.store")} (${magCount})`} />
                <LegendItem color="#B4B2A9" shape="circle" label={`${t("containers.timeline.road")} (${roadCount})`} />
                <LegendItem color="#1D9E75" shape="tri-up" label={t("containers.timeline.loading")} />
                <LegendItem color="#BA7517" shape="tri-down" label={t("containers.timeline.unloading")} />
            </div>

            <div style={timelineStyles.track}>
                <div style={timelineStyles.trackBackground} />
                <div style={timelineStyles.axis} />

                {dateLabels.map((dl, i) => {
                    const left = pct(new Date(dl.ts));
                    return (
                        <React.Fragment key={i}>
                            <div style={tickStyle(left)} />
                            <div style={tickLabelStyle(left)}>{dl.label}</div>
                        </React.Fragment>
                    );
                })}

                {sorted.map(p => {
                    const left = pct(p.lastActiveDate);
                    const isSelected = selectedPoint !== undefined && selectedPoint.id === p.id;
                    const color = p.internalStoreName ? '#7F77DD' : p.receptionPlaceName ? '#378ADD' : '#B4B2A9';
                    const isSquare = !!p.internalStoreName;
                    return (
                        <div
                            key={p.id}
                            onClick={() => onSelectPoint(p)}
                            onMouseEnter={() => setHoverText(gpsSummary(p, t))}
                            onMouseLeave={handleMouseLeave}
                            style={historyPointStyle(left, isSelected, color, isSquare)}
                        />
                    );
                })}

                {scanEvents.map((ev, i) => {
                    const left = pct(ev.date);
                    const isLoad = ev.type === 'loading';
                    const color = isLoad ? '#1D9E75' : '#BA7517';
                    const tipTop = isLoad ? MARKER_TOP : MARKER_TOP + 12;
                    return (
                        <React.Fragment key={i}>
                            <div style={scanLineStyle(left, tipTop, color)} />
                            <div
                                onMouseEnter={() => setHoverText(scanSummary(ev, t))}
                                onMouseLeave={handleMouseLeave}
                                style={scanMarkerStyle(left, isLoad, color)}
                            />
                        </React.Fragment>
                    );
                })}
            </div>

            <div style={infoLineStyle}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {hoverText || ' '}
                </span>
                {hoverText && (
                    <button
                        onClick={handleCopy}
                        title={t("containers.timeline.copy")}
                        style={{
                            marginLeft: 8,
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: 13,
                            color: copied ? '#1D9E75' : '#a19f9d',
                            padding: '0 2px',
                            flexShrink: 0,
                        }}
                    >
                        {copied ? '✓' : '⎘'}
                    </button>
                )}
            </div>
        </div>
    );
};

const LegendItem: React.FC<{ color: string; shape: string; label: string }> = ({ color, shape, label }) => (
    <div style={timelineStyles.legendItem}>
        {shape === 'circle' && <div style={{ width: 9, height: 9, borderRadius: '50%', background: color, flexShrink: 0 }} />}
        {shape === 'square' && <div style={{ width: 9, height: 9, borderRadius: 2, background: color, flexShrink: 0 }} />}
        {shape === 'tri-up' && <div style={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderBottom: `10px solid ${color}`, flexShrink: 0 }} />}
        {shape === 'tri-down' && <div style={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: `10px solid ${color}`, flexShrink: 0 }} />}
        <span>{label}</span>
    </div>
);

function gpsSummary(p: IContainerHistoryPoint, t: TFunction): string {
    const time = moment(p.lastActiveDate).format('DD.MM.YYYY HH:mm');
    if (p.internalStoreName) return `${t("containers.timeline.store")}: ${p.internalStoreName} — ${time}`;
    if (p.receptionPlaceName) return `${t("containers.timeline.receptionPlace")}: ${p.receptionPlaceName} — ${time}`;
    return `${t("containers.timeline.road")} — ${time}`;
}

function scanSummary(ev: IContainerScanEvent, t: TFunction): string {
    const time = moment(ev.date).format('DD.MM.YYYY HH:mm');
    const label = ev.type === 'loading' ? t("containers.timeline.loading") : t("containers.timeline.unloading");
    const parts = [label, ev.receptionPlaceName, ev.containerNumber ? `${t("containers.timeline.container")}: ${ev.containerNumber}` : ''].filter(Boolean);
    return `${parts.join(' | ')} — ${time}`;
}

function buildDateLabels(minDate: number, maxDate: number): { ts: number; label: string }[] {
    const spanDays = (maxDate - minDate) / (1000 * 60 * 60 * 24);
    const labels: { ts: number; label: string }[] = [];
    const step = spanDays > 180 ? 14 : spanDays > 60 ? 7 : spanDays > 21 ? 3 : 1;
    const cur = new Date(minDate);
    cur.setHours(0, 0, 0, 0);
    while (cur.getTime() <= maxDate) {
        labels.push({ ts: cur.getTime(), label: moment(cur).format('DD.MM') });
        cur.setDate(cur.getDate() + step);
    }
    return labels;
}

export default ContainerTimeline;
